import { CommissionStep, Loan, Role } from '../types/commission';

export function formatCurrency(value: number, maximumFractionDigits = 0): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits,
    minimumFractionDigits: 0,
  }).format(value);
}

export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(new Date(`${iso}T12:00:00`));
}

/** "105 BPS" or "102.5 BPS". */
export function formatBps(value: number): string {
  const rounded = Math.round(value * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded : rounded.toFixed(1)} BPS`;
}

/** "+2.5 BPS" / "−3 BPS". */
export function formatSignedBps(value: number): string {
  const sign = value > 0 ? '+' : value < 0 ? '−' : '';
  return `${sign}${formatBps(Math.abs(value))}`;
}

/** The schedule row a unit count lands in. */
export function stepForUnits(role: Role, units: number): CommissionStep {
  return role.steps.reduce((acc, step) => (units >= step.minUnits ? step : acc), role.steps[0]);
}

/** The next schedule row above the current unit count, if any. */
export function nextStepForUnits(role: Role, units: number): CommissionStep | null {
  return role.steps.find((step) => units < step.minUnits) ?? null;
}

/**
 * The row actually being paid. Roles gated on the scorecard drop back to their
 * base row when the monthly scorecard goal is missed.
 */
export function payingStep(role: Role, units: number, scorecardMet: boolean): CommissionStep {
  if (role.requiresScorecard && !scorecardMet) return role.steps[0];
  return stepForUnits(role, units);
}

export function stepRangeLabel(step: CommissionStep, unitNounPlural: string): string {
  if (step.maxUnits === null) return `${step.minUnits}+ ${unitNounPlural}`;
  return `${step.minUnits}–${step.maxUnits} ${unitNounPlural}`;
}

export function stepShortLabel(step: CommissionStep): string {
  return step.maxUnits === null ? `${step.minUnits}+` : `${step.minUnits}–${step.maxUnits}`;
}

/** "105 BPS" or "$35". */
export function stepPayoutLabel(role: Role, step: CommissionStep): string {
  if (role.model === 'bps') return formatBps(step.totalBps ?? 0);
  return formatCurrency(step.perUnit ?? 0);
}

export function bonusLabel(role: Role, step: CommissionStep): string {
  if (role.model === 'bps') return step.bonusBps ? `+${step.bonusBps} BPS` : '—';
  return `${formatCurrency(step.perUnit ?? 0)} per ${role.incentiveNoun ?? 'unit'}`;
}

/** Commission for one line item at the rate it is actually paid. */
export function lineCommission(role: Role, step: CommissionStep, loan: Loan, bps: number | null): number {
  const share = loan.commissionPercentage / 100;
  if (role.model === 'bps') return ((loan.amount * (bps ?? 0)) / 10000) * share;
  return (step.perUnit ?? 0) * share;
}

export interface CommissionTotal {
  gross: number;
  net: number;
  capped: boolean;
}

export function capTotal(role: Role, gross: number): CommissionTotal {
  const capped = role.monthlyCap !== undefined && gross > role.monthlyCap;
  return { gross, net: capped ? (role.monthlyCap as number) : gross, capped };
}

export function sumVolume(loans: Loan[]): number {
  return loans.reduce((total, loan) => total + loan.amount, 0);
}
